import { Box, ButtonBase, LinearProgress, TextField, Typography } from '@mui/material';
import { getReportLanguage } from '../components/reportLanguageSelector';
import { Icon } from '@iconify/react';
import { makeSignedS3Url } from '../../common/helpers/makeSignedS3Url';
import { ReportSectionComponentType } from '../types';
import { UPLOADED_LOGO_LABEL, useReportUpdater } from '../hooks/useReportUpdater';
import { useDropzone } from 'react-dropzone';
import { useUploadReportFile } from '../../common/components/mutations';
import EmptySectionWarning from '../components/emptySectionWarning';
import ReportPreviewButton from '../components/reportPreviewButton';
import StepFooter from '../../common/components/stepFooter';

/**
 * Report cover step
 */
const CoverPageStep: ReportSectionComponentType = ({
  sectionName,
  report,
  onClickBack,
  onClickNext,
  isLoading: isSomethingLoading,
}) => {
  const { mutateAsync: uploadFileMutation, isPending: uploadingFile } = useUploadReportFile();
  const { isLoading, saveChanges, setReportData, sectionData, reportData, addFile } =
    useReportUpdater({ report, sectionName });
  const logoFile = reportData?.files.find(({ fileLabel }) => fileLabel === UPLOADED_LOGO_LABEL);

  const onDrop = ([file]: File[]): void => {
    if (!file || !sectionData) {
      return;
    }
    uploadFileMutation({ file, reportId: sectionData.reportID }).then((uploadedFiles) => {
      uploadedFiles.map(({ url, name }) => addFile(url, name, UPLOADED_LOGO_LABEL, true, true));
    });
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    disabled: isLoading,
    multiple: false,
    accept: {
      'image/*': ['.jpeg', '.png'],
    },
  });

  const handleClickNext = (): void => {
    saveChanges().then(() => onClickNext());
  };

  if (!sectionData) {
    return <EmptySectionWarning />;
  }

  return (
    <Box>
      <Box className="flex space-x-8 mb-4">
        <Box>
          <ButtonBase
            {...getRootProps()}
            className="flex-col h-full w-64"
            sx={{
              color: 'primary.main',
              border: 1,
              borderStyle: 'dashed',
              borderColor: '#325B853D',
            }}
          >
            <input {...getInputProps()} />
            {logoFile ? (
              <img src={makeSignedS3Url(logoFile.fileUrl)} alt="" className="object-contain" />
            ) : (
              <Box sx={{ borderRadius: 3, bgcolor: '#F8FAFD', p: 3 }}>
                <Box className="flex items-center flex-col">
                  <Icon icon="icon-park-outline:upload-two" width={16} />
                  <Typography fontWeight={500}>Upload logo</Typography>
                </Box>
              </Box>
            )}
            {uploadingFile && <LinearProgress className="mt-1 w-32" />}
          </ButtonBase>
        </Box>
        <Box className="space-y-4 flex-grow">
          <TextField
            fullWidth
            value={reportData.name ?? ''}
            label="Report Title *"
            onChange={({ target: { value } }): void =>
              setReportData({ ...reportData, name: value })
            }
          />
          <TextField
            fullWidth
            multiline
            rows={5}
            value={reportData.desc ?? ''}
            label="Report Description *"
            onChange={({ target: { value } }): void =>
              setReportData({ ...reportData, desc: value })
            }
          />
        </Box>
      </Box>
      <ReportPreviewButton
        isLoading={isLoading}
        language={getReportLanguage(report)}
        onClickPreview={saveChanges}
        reportId={report.id}
        sectionName={sectionName}
      />
      <StepFooter
        isLoading={isLoading || isSomethingLoading}
        onClickBack={onClickBack}
        onClickNext={handleClickNext}
      />
    </Box>
  );
};

export default CoverPageStep;
